import React, { useState, useRef, useEffect, useCallback } from 'react';
import client from '../../api/client';
import styles from './OnboardingChatBar.module.css';

const getGreeting = (mode) => {
  switch (mode) {
    case 'teacher-welcome':
      return "Tell me about your teaching style! What methods do you prefer? Any classroom dynamics I should know about?";
    case 'class-onboarding':
      return "Any special requests for this class? Topics to emphasize, common struggles, testing prep?";
    case 'dashboard':
      return "What would you like to work on today?";
    default:
      return "Tell me about your class and I'll help fill in the form!";
  }
};

export default function OnboardingChatBar({ onFormFieldUpdate, conversationId, onConversationId, onScopeData, classId, mode = 'onboarding', profileId = null }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: getGreeting(mode) },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeDocument, setActiveDocument] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (expanded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, expanded]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF and image files (JPEG, PNG, WebP, GIF) are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5MB');
      return;
    }
    setSelectedFile(file);
    e.target.value = '';
  };

  const removeSelectedFile = () => setSelectedFile(null);

  const removeActiveDocument = async () => {
    if (activeDocument?.classId) {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await client.delete('/api/chat/document', { headers, params: { classId: activeDocument.classId } });
      } catch (e) { /* best effort */ }
    }
    setActiveDocument(null);
  };

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() && !selectedFile) return;
    const userMsg = text.trim();

    setMessages(prev => [...prev, { role: 'user', text: userMsg, fileName: selectedFile?.name }]);
    setInput('');
    setLoading(true);
    setExpanded(true);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let response;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('message', userMsg);
        if (conversationId) formData.append('conversationId', conversationId);
        formData.append('mode', mode);
        if (profileId) formData.append('profileId', String(profileId));
        if (classId) formData.append('classId', classId);
        formData.append('document', selectedFile);
        response = await client.post('/api/chat', formData, { headers });
        setActiveDocument({ name: selectedFile.name, classId });
        setSelectedFile(null);
      } else {
        response = await client.post('/api/chat', {
          message: userMsg,
          conversationId,
          mode,
          ...(profileId ? { profileId } : {}),
        }, { headers });
      }

      if (response.data.text) {
        setMessages(prev => [...prev, { role: 'assistant', text: response.data.text }]);
      }

      if (response.data.conversationId && onConversationId) {
        onConversationId(response.data.conversationId);
      }

      // Process lessonPlanUpdates for form field updates
      if (response.data.lessonPlanUpdates) {
        for (const update of response.data.lessonPlanUpdates) {
          if (update.section === 'formFieldUpdates' && onFormFieldUpdate) {
            onFormFieldUpdate(update.content);
          }
          if (update.section === 'scopeSequence' && onScopeData) {
            onScopeData(update.content);
          }
          if (update.section === 'profileComplete' && onFormFieldUpdate) {
            onFormFieldUpdate(update.content);
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [conversationId, onConversationId, onFormFieldUpdate, onScopeData, selectedFile, classId, mode, profileId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  useEffect(() => {
    return () => {
      if (activeDocument?.classId) {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        client.delete('/api/chat/document', { headers, params: { classId: activeDocument.classId } }).catch(() => {});
      }
    };
  }, [activeDocument]);

  return (
    <div className={styles.chatBar}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      {/* File chip and active document indicator */}
      {selectedFile && (
        <div className={styles.fileChip}>
          <span className={styles.fileIcon}>📄</span>
          <span className={styles.fileName}>{selectedFile.name}</span>
          <button type="button" className={styles.fileRemove} onClick={removeSelectedFile}>×</button>
        </div>
      )}
      {activeDocument && !selectedFile && (
        <div className={styles.activeDoc}>
          <span className={styles.activeDocIcon}>📎</span>
          <span>Document active: {activeDocument.name}</span>
          <button type="button" className={styles.fileRemove} onClick={removeActiveDocument}>×</button>
        </div>
      )}
      {/* Expandable history */}
      {expanded && (
        <div className={styles.history}>
          <button
            className={styles.collapseBtn}
            onClick={() => setExpanded(false)}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 15 12 9 18 15" />
            </svg>
            Hide chat
          </button>
          <div className={styles.messageList}>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`${styles.message} ${msg.role === 'assistant' ? styles.msgAssistant : styles.msgUser}`}
              >
                {msg.role === 'assistant' && (
                  <img src="/logo-notext.svg" alt="Teachio" className={styles.aiLabel} />
                )}
                <span className={styles.msgText}>{msg.text}</span>
                {msg.fileName && (
                  <span className={styles.fileBadge}>📄 {msg.fileName}</span>
                )}
              </div>
            ))}
            {loading && (
              <div className={`${styles.message} ${styles.msgAssistant}`}>
                <img src="/logo-notext.svg" alt="Teachio" className={styles.aiLabel} />
                <span className={styles.typing}>
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Input bar */}
      <div className={styles.inputBar}>
        {!expanded && messages.length > 1 && (
          <button
            className={styles.expandBtn}
            onClick={() => setExpanded(true)}
            type="button"
          >
            {messages.length - 1} message{messages.length > 2 ? 's' : ''}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 15 12 9 18 15" />
            </svg>
          </button>
        )}
        <div className={styles.inputRow}>
          <div className={styles.inputIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <button
            type="button"
            className={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            title="Attach a document"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (messages.length > 1) setExpanded(true); }}
            placeholder="Need help? Ask me anything..."
            className={styles.textInput}
            disabled={loading}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            className={styles.sendBtn}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
